import type { ReviewMethodRequestType } from './parseReply';

export type ReviewMethodOption = {
    value: ReviewMethodRequestType;
    name: string;
};

export type ReviewMethodOptions = {
    title?: string;
    options: ReviewMethodOption[];
    preselectedMethod?: ReviewMethodRequestType;
    /** Optional: pre-fill the appointment date on step 2. Format: YYYY-MM-DD. */
    preselectedDate?: string;
    /** Optional: pre-fill the appointment timezone on step 2. Format: IANA timezone string. */
    preselectedTimezone?: string;
    /** Optional: pre-fill the appointment time on step 3. */
    preselectedTime?: string;
    /** Optional: scope availability queries to a specific designer's calendar. */
    calendarId?: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseReviewMethodOption(item: unknown): ReviewMethodOption | null {
    if (!isRecord(item)) return null;

    const { value, name } = item;

    if (value !== 'Standard' && value !== 'DesignLive') return null;
    if (typeof name !== 'string' || name === '') return null;

    return { value, name };
}

/**
 * Parses the assistant-side review-method question payload that drives the composer selector.
 *
 * Accepts `unknown` input and returns a validated `ReviewMethodOptions` or `null` on any
 * validation failure. Lenient on unknown fields; only known fields are extracted.
 *
 * @param data - The input to parse (expected to be the assistant's `component.data`)
 * @returns A validated `ReviewMethodOptions` object, or `null` if validation fails
 */
export function parseReviewMethodOptions(data: unknown): ReviewMethodOptions | null {
    if (!isRecord(data)) {
        return null;
    }

    const {
        title,
        options,
        preselectedMethod,
        preselectedDate,
        preselectedTime,
        preselectedTimezone,
        calendarId,
    } = data;

    if (!Array.isArray(options) || options.length === 0) {
        return null;
    }

    const parsedOptions: ReviewMethodOption[] = [];
    for (const item of options) {
        const parsed = parseReviewMethodOption(item);
        if (!parsed) return null;
        parsedOptions.push(parsed);
    }

    if (title !== undefined && typeof title !== 'string') return null;
    if (
        preselectedMethod !== undefined &&
        preselectedMethod !== 'Standard' &&
        preselectedMethod !== 'DesignLive'
    )
        return null;
    if (preselectedDate !== undefined && typeof preselectedDate !== 'string') return null;
    if (preselectedTime !== undefined && typeof preselectedTime !== 'string') return null;
    if (preselectedTimezone !== undefined && typeof preselectedTimezone !== 'string') return null;
    if (calendarId !== undefined && typeof calendarId !== 'number') return null;

    return {
        ...(title !== undefined ? { title } : {}),
        options: parsedOptions,
        ...(preselectedMethod !== undefined ? { preselectedMethod } : {}),
        ...(preselectedDate !== undefined ? { preselectedDate } : {}),
        ...(preselectedTime !== undefined ? { preselectedTime } : {}),
        ...(preselectedTimezone !== undefined ? { preselectedTimezone } : {}),
        ...(calendarId !== undefined ? { calendarId } : {}),
    };
}
